import { useMemo, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { ChevronsUpDown, LogOut, PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { useModuleAccess } from "@/features/plan/hooks";
import { LockBadge } from "@/features/plan/Locked";
import { useOrg } from "@/stores/org";
import { useSession } from "@/stores/session";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { GROUP_LABEL, NAV } from "@/app/navigation";
import type { NavEntry } from "@/app/navigation";

type EntradaMenu = NavEntry & { bloqueado: boolean };
import { Skeleton } from "@/components/ui/states";

export function AppShell() {
  const { org, memberships } = useActiveOrg();
  const { data: acceso, isPending } = useModuleAccess();
  const collapsed = useOrg((s) => s.sidebarCollapsed);
  const toggle = useOrg((s) => s.toggleSidebar);

  // El menu es el cruce del catalogo de rutas con lo que la tienda tiene.
  // Nada esta escrito a mano.
  //
  // Ya no se filtra por lo contratado: se marca. Un modulo que el plan no
  // incluye sigue en la lista con su candado, porque esconderlo deja al
  // cliente sin saber que existe. Quien decide si puede usarlo es la base.
  const entries = useMemo(() => {
    const orden = new Map((acceso ?? []).map((m) => [m.key, m.sortOrder]));
    const abiertos = new Set((acceso ?? []).filter((m) => m.contratado).map((m) => m.key));
    const conocidos = new Set((acceso ?? []).map((m) => m.key));

    return NAV.filter((e) => conocidos.has(e.module))
      .map((e) => ({ ...e, bloqueado: !abiertos.has(e.module) }))
      .sort((a, b) => (orden.get(a.module) ?? 0) - (orden.get(b.module) ?? 0));
  }, [acceso]);

  const groups = useMemo(() => {
    const byGroup = new Map<NavEntry["group"], EntradaMenu[]>();
    for (const e of entries) {
      byGroup.set(e.group, [...(byGroup.get(e.group) ?? []), e]);
    }
    return [...byGroup];
  }, [entries]);

  return (
    <div className="flex min-h-dvh bg-ground">
      {/* ------------------------------------------------------- sidebar */}
      <aside
        className={cn(
          "hidden shrink-0 flex-col border-r border-line bg-sunk transition-[width] md:flex",
          collapsed ? "w-[56px]" : "w-[216px]",
        )}
      >
        <TenantChip collapsed={collapsed} />

        <nav className="flex flex-col gap-px p-2">
          {isPending && !collapsed && (
            <div className="flex flex-col gap-2 p-2">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          )}

          {groups.map(([group, items]) => (
            <div key={group} className="flex flex-col gap-px">
              {GROUP_LABEL[group] && !collapsed && (
                <span className="px-2 pb-1 pt-3 font-mono text-mark uppercase tracking-[0.14em] text-ink-3">
                  {GROUP_LABEL[group]}
                </span>
              )}
              {items.map((entry) => (
                <NavItem key={entry.path} entry={entry} collapsed={collapsed} />
              ))}
            </div>
          ))}
        </nav>

        <button
          type="button"
          onClick={toggle}
          className="mt-auto flex items-center gap-2 px-3.5 py-3 text-ink-3 hover:text-ink-2"
          aria-label={collapsed ? "Expandir menú" : "Plegar menú"}
        >
          {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          {!collapsed && <span className="text-xs">Plegar</span>}
        </button>
      </aside>

      {/* ---------------------------------------------------------- main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar orgName={org?.storeName ?? org?.name ?? ""} canSwitch={memberships.length > 1} />
        <main className="flex-1 p-4 pb-20 md:pb-4">
          <Outlet />
        </main>
        <MobileTabs entries={entries.filter((e) => e.mobile && !e.bloqueado)} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ pieces */

function NavItem({ entry, collapsed }: { entry: EntradaMenu; collapsed: boolean }) {
  const Icon = entry.icon;

  return (
    <NavLink
      to={entry.path}
      end={entry.path === "/"}
      title={
        collapsed
          ? entry.bloqueado
            ? `${entry.label} · necesita otro plan`
            : entry.label
          : undefined
      }
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-ui transition-colors",
          isActive
            ? "bg-accent-soft font-semibold text-accent"
            : entry.bloqueado
              ? "text-ink-3 hover:bg-line-2"
              : "text-ink-2 hover:bg-line-2 hover:text-ink",
          collapsed && "justify-center px-0",
        )
      }
    >
      <Icon className="size-[15px] shrink-0" strokeWidth={1.8} />
      {!collapsed && (
        <>
          <span className="truncate">{entry.label}</span>
          {entry.bloqueado && <LockBadge className="ml-auto" />}
        </>
      )}
    </NavLink>
  );
}

function TenantChip({ collapsed }: { collapsed: boolean }) {
  const { org, memberships } = useActiveOrg();
  const setActiveOrg = useOrg((s) => s.setActiveOrg);
  const [open, setOpen] = useState(false);

  if (!org) {
    return <div className="border-b border-line p-3"><Skeleton className="h-6 w-full" /></div>;
  }

  const initial = (org.storeName ?? org.name).charAt(0).toUpperCase();

  return (
    <div className="relative border-b border-line">
      <button
        type="button"
        onClick={() => memberships.length > 1 && setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2.5 px-3.5 py-3 text-left",
          memberships.length > 1 && "hover:bg-line-2",
        )}
        aria-haspopup={memberships.length > 1 ? "listbox" : undefined}
        aria-expanded={memberships.length > 1 ? open : undefined}
      >
        {/* El color del tenant se usa aqui y solo aqui: es identidad, no
            interfaz. El resto del panel es de Synkro. */}
        <span
          className="grid size-[26px] shrink-0 place-items-center rounded-[7px] text-xs font-bold text-white"
          style={{ background: org.primaryColor }}
        >
          {initial}
        </span>
        {!collapsed && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-ui font-semibold leading-tight">
              {org.storeName ?? org.name}
            </span>
            <span className="block truncate font-mono text-mark text-ink-3">
              {org.primaryDomain ?? org.slug}
            </span>
          </span>
        )}
        {!collapsed && memberships.length > 1 && (
          <ChevronsUpDown className="size-3.5 shrink-0 text-ink-3" />
        )}
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute inset-x-2 top-full z-20 mt-1 overflow-hidden rounded-md border border-line bg-raised shadow-lg"
        >
          {memberships.map((m) => (
            <li key={m.organizationId}>
              <button
                type="button"
                role="option"
                aria-selected={m.organizationId === org.organizationId}
                onClick={() => {
                  setActiveOrg(m.organizationId);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-ui hover:bg-sunk"
              >
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: m.primaryColor }}
                />
                <span className="truncate">{m.storeName ?? m.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Topbar({ orgName, canSwitch }: { orgName: string; canSwitch: boolean }) {
  const email = useSession((s) => s.session?.user.email ?? "");
  const initials = email.slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-10 flex h-[52px] items-center gap-3 border-b border-line bg-surface px-4">
      <span className="truncate text-sm font-semibold md:hidden">{orgName}</span>

      <button
        type="button"
        className="ml-auto hidden max-w-[320px] flex-1 items-center gap-2 rounded-md border border-line bg-sunk px-2.5 py-1.5 text-left text-meta text-ink-3 md:ml-0 md:flex"
      >
        <Search className="size-3.5" />
        Buscar pedido, cliente o SKU
        <kbd className="ml-auto rounded border border-line px-1 font-mono text-mark">Ctrl K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        <NotificationBell />
        <button
          type="button"
          onClick={() => void supabase.auth.signOut()}
          className="grid size-8 place-items-center rounded-md text-ink-2 hover:bg-sunk"
          aria-label="Cerrar sesión"
          title={email}
        >
          <LogOut className="size-4" strokeWidth={1.8} />
        </button>
        <span
          className="grid size-[26px] place-items-center rounded-full bg-accent text-meta font-bold text-accent-ink"
          title={canSwitch ? "Varias organizaciones" : email}
        >
          {initials}
        </span>
      </div>
    </header>
  );
}

/**
 * En el telefono el panel hace una cosa: mirar lo que entro. Barra inferior
 * con los destinos frecuentes, no un menu hamburguesa.
 */
function MobileTabs({ entries }: { entries: NavEntry[] }) {
  if (entries.length === 0) return null;

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-20 grid border-t border-line bg-surface md:hidden"
      style={{ gridTemplateColumns: `repeat(${entries.length}, 1fr)` }}
    >
      {entries.map((entry) => {
        const Icon = entry.icon;
        return (
          <NavLink
            key={entry.path}
            to={entry.path}
            end={entry.path === "/"}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-1 py-2 pb-3 text-mark",
                isActive ? "text-accent" : "text-ink-3",
              )
            }
          >
            <Icon className="size-[17px]" strokeWidth={1.8} />
            {entry.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
